/**
 * Specification §3.7 (DECIDED 20261023): the postcode fills in the town, on
 * the delivery and the billing address.
 *
 * - One settlement: the town is filled in.
 * - Several: the town field offers them as a short list.
 * - A postcode starting with 1: *Budapest*.
 * - The town stays editable, and a town the customer has **typed** is never
 *   overwritten; a later postcode change offers the looked-up town beside it
 *   instead.
 * - An unknown postcode leaves the town empty and editable (for delivery the
 *   zone then follows §3.7's no-coordinates rule, which is the server's).
 *
 * The table is Magyar Posta's list as `scripts/build_postcodes.mjs` writes it
 * into `src/lib/postcodes.json`: postcode → settlements, and nothing else.
 * Everything here is pure, so the page and the tests read the same rules.
 */

import type { AddressFields } from './state.ts'

/** postcode → settlements, in the list's own order. */
export type TownTable = Readonly<Record<string, readonly string[]>>

let loading: Promise<TownTable> | null = null

/**
 * The table, loaded once and on demand: a separate chunk, so the first paint
 * does not wait for seventy kilobytes of place names.
 */
export function loadTownTable(): Promise<TownTable> {
  loading ??= import('../lib/postcodes.json').then(
    (module) => (module as unknown as { default: TownTable }).default,
  )
  return loading
}

const BUDAPEST = 'Budapest'

/**
 * The settlements a postcode stands for.
 *
 * `null` while there is nothing to look up — not four digits yet, or a
 * non-Budapest postcode before the table has loaded — and an empty list for a
 * postcode the list does not know.
 */
export function townsFor(table: TownTable | null, postcode: string): readonly string[] | null {
  const code = postcode.trim()
  if (!/^\d{4}$/.test(code)) return null
  if (code.startsWith('1')) return [BUDAPEST]
  if (table === null) return null
  return table[code] ?? []
}

/**
 * The address after its postcode changed (or after the table arrived, with the
 * same postcode). The postcode is always taken; the town only where the
 * customer has not typed one.
 */
export function withPostcode(
  address: AddressFields,
  postcode: string,
  table: TownTable | null,
): AddressFields {
  const next = { ...address, postcode }
  if (next.townTyped === true) return next
  const towns = townsFor(table, postcode)
  if (towns === null) return next
  if (towns.length === 1) return { ...next, town: towns[0]! }
  /* Several: the town is one of them only once the customer picks it. One
     already picked from this same list stays picked. Unknown: empty. */
  return { ...next, town: towns.includes(next.town) ? next.town : '' }
}

/**
 * What the town field offers under itself: the looked-up settlements that are
 * not already in it. Several settlements are always offered until one is in
 * the field; a single one only where the customer typed something else.
 */
export function townOffers(address: AddressFields, table: TownTable | null): readonly string[] {
  const towns = townsFor(table, address.postcode)
  if (towns === null || towns.length === 0) return []
  if (towns.includes(address.town)) return []
  if (towns.length > 1 || address.townTyped === true) return towns
  return []
}

/** The customer typed in the town field. An emptied field is free again. */
export const typedTown = (address: AddressFields, town: string): AddressFields => ({
  ...address,
  town,
  townTyped: town.trim() !== '',
})

/** The customer picked one of the offered settlements: the lookup's answer. */
export const pickedTown = (address: AddressFields, town: string): AddressFields => ({
  ...address,
  town,
  townTyped: false,
})
